import { DeviceMetricsTable } from './device-metrics-table.tsx';
import { tableColumns } from './device-metrics-table.columns.tsx';
import { DeviceMetricsTableRowSubComponent } from './device-metrics-table-row-sub-component.tsx';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { cn } from '@/utils/utils.ts';
import { useCallback, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { Button } from '@/components/ui/button.tsx';
import { useSession } from '@/services/providers/use-session.ts';
import { DeviceFieldsPickerDialog } from '@/features/devices/components/device-fields-picker-dialog/device-fields-picker-dialog.tsx';
import { TGetDeviceResponse } from '@/features/devices/hooks/queries/[device]/get-device.ts';
import { TDeviceOverviewConfig } from '@/features/devices/hooks/queries/[device]/get-device-overview.ts';
import { DeviceTypeCodes } from '@/features/devices/schemas/devices.schemas.ts';

type TDeviceMetric = {
  unit: string;
  fieldCode: number;
  deviceTypeCode: DeviceTypeCodes;
  aggregation: string;
  time: string;
  value: number;
  last24Hours: TDeviceMetric[];
};

const EMPTY_DATA: TDeviceMetric[] = [];

export const DeviceMetricsTableCard = ({
  data,
  device,
  config,
  title,
  isLoading,
  className,
  showSelectFields = false,
  expandable = {
    isExpandable: false,
    defaultExpanded: true,
  },
}: {
  data: TDeviceMetric[] | undefined;
  device: TGetDeviceResponse | undefined;
  config?: TDeviceOverviewConfig;
  title?: string;
  isLoading: boolean;
  className?: string;
  showSelectFields?: boolean;
  expandable?: {
    isExpandable: boolean;
    defaultExpanded: boolean;
    onExpandChange?: (isExpanded: boolean) => void;
  };
}) => {
  const [isExpanded, setIsExpanded] = useState(expandable.defaultExpanded);
  const cardRef = useRef<HTMLDivElement>(null);

  const { role } = useSession();

  const handleClick = useCallback(() => {
    setIsExpanded((prev) => {
      const newValue = !prev;
      expandable.onExpandChange?.(newValue);
      return newValue;
    });
    setTimeout(() => {
      if (cardRef.current) {
        cardRef.current.focus();
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }, [expandable]);

  return (
    <DashboardCard
      className={cn(isExpanded && 'min-h-[464px]', className)}
      isLoading={isLoading}
    >
      <div ref={cardRef}>
        <div>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xl font-semibold text-nowrap">
              {title ? title : `Overview ${device?.type}`}
            </h3>
            <div>
              {showSelectFields && device && (
                <DeviceFieldsPickerDialog
                  deviceId={device.deviceId}
                  deviceType={device.type}
                  plantId={device.plantId}
                  config={config}
                />
              )}
              {expandable.isExpandable && (
                <Button variant="secondary" className="p-2" onClick={handleClick}>
                  {isExpanded ? (
                    <ChevronUpIcon className="size-5" />
                  ) : (
                    <ChevronDownIcon className="size-5" />
                  )}
                </Button>
              )}
            </div>
          </div>
          <div
            className={cn(
              'grid overflow-hidden transition-all duration-300 ease-in-out',
              isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            {device && isExpanded && (
              <DeviceMetricsTable
                data={data ?? EMPTY_DATA}
                columns={tableColumns(device.type, role)}
                getRowCanExpand={() => true}
                RenderSubComponent={DeviceMetricsTableRowSubComponent}
                plantId={device.plantId}
                deviceType={device.type}
                deviceSN={device.sn}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardCard>
  );
};
